import React, { FunctionComponent, ReactNode, useEffect, useRef, useState } from 'react'
import { ViewStyle } from 'react-native'

import styled from 'styled-components/native'

import { Spacing } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/spacing'

import { HorizontalTabs, IndicatorVariant, TextCasing } from '../../molecules/horizontalTabs/HorizontalTabs'
import { tID } from '../../utils/utils'

export type TabItem = {
  id: string
  label: string
  content: ReactNode
  overflowTreatment?: string
  iconWithTooltip?: ReactNode
  /** When provided and > 0, renders a red counter badge next to the tab label. */
  badgeCount?: number
}

export type HorizontalTabsContainerProps = {
  tabs: TabItem[]
  initialTabId?: string
  /** Optional externally-driven active tab: when this prop changes to a new id, the container switches to it. Tab presses still update internal state and notify via `onTabChange`. Omit for fully internal tab state. */
  activeTabId?: string
  onTabChange?: (tabId: string) => void
  testID?: string
  style?: ViewStyle
  contentStyle?: ViewStyle
  /** Hide the tab bar while still rendering the active tab's content. Useful when only a single tab remains and the bar would be meaningless. */
  hideTabBar?: boolean
  tabBarOptions?: {
    /** Hides the bottom margin on the tabs */
    hideBottomMargin?: boolean
    tabHeight?: number
    tabHorizontalPadding?: keyof Spacing
    indicatorVariant?: IndicatorVariant
    textCasing?: TextCasing
    showBorder?: boolean
    /** Override the pressed opacity for tab items */
    pressedOpacity?: number
    /** Override the active indicator height (default: '4px') */
    indicatorHeight?: string
    /** Override the active indicator border radius (default: theme.spacing['32px']) */
    indicatorBorderRadius?: string
    /** Override the active indicator background color */
    indicatorColor?: string
    /** Override the inactive tab text color */
    inactiveTextColor?: string
    /** Override the default tab item style (e.g. paddingVertical) */
    tabStyle?: ViewStyle
  }
}

const Container = styled.View(({ theme }) => ({
  flexDirection: 'column',
  backgroundColor: theme.colors.backgroundPrimary,
  flex: 1,
  overflow: 'visible',
}))

const TabsWrapper = styled.View<{
  hideBottomMargin?: boolean
  tabHeight?: number
  tabHorizontalPadding?: keyof Spacing
  showBorder?: boolean
}>(({ theme, hideBottomMargin, tabHeight, tabHorizontalPadding, showBorder }) => ({
  borderBottomWidth: showBorder ? '1px' : '0px',
  borderBottomColor: theme.colors.borderDefault,
  paddingLeft: tabHorizontalPadding ? theme.spacing[tabHorizontalPadding] : theme.spacing['16px'],
  paddingRight: tabHorizontalPadding ? theme.spacing[tabHorizontalPadding] : theme.spacing['16px'],
  ...(!hideBottomMargin && { marginBottom: theme.spacing['16px'] }),
  ...(tabHeight && { height: `${tabHeight}px` }),
  flex: '0 0 auto',
}))

const ContentContainer = styled.View<{ overflowTreatment?: string }>(({ overflowTreatment }) => ({
  border: '0px',
  padding: '0px',
  flex: '1 1 auto',
  flexDirection: 'column',
  overflow: overflowTreatment,
}))

export const HorizontalTabsContainer: FunctionComponent<HorizontalTabsContainerProps> = ({
  tabs,
  initialTabId,
  activeTabId: controlledTabId,
  onTabChange,
  testID = 'HorizontalTabsContainer',
  style,
  contentStyle,
  tabBarOptions,
  hideTabBar,
}) => {
  const [activeTabId, setActiveTabId] = useState(controlledTabId || initialTabId || (tabs.length > 0 ? tabs[0].id : ''))
  const activeTabIdRef = useRef(activeTabId)
  activeTabIdRef.current = activeTabId
  const lastControlledTabId = useRef(controlledTabId)

  // Sync external `activeTabId` changes in through the same bookkeeping as a press. Only prop
  // *transitions* are applied, so a press is not bounced back while the prop briefly lags the
  // internal state.
  useEffect(() => {
    const previous = lastControlledTabId.current
    lastControlledTabId.current = controlledTabId
    if (!controlledTabId || controlledTabId === previous || controlledTabId === activeTabIdRef.current) {
      return
    }
    setActiveTabId(controlledTabId)
  }, [controlledTabId])

  const handleTabPress = (tabId: string) => {
    setActiveTabId(tabId)
    onTabChange?.(tabId)
  }

  const activeTab = tabs.find((tab) => tab.id === activeTabId)
  return (
    <Container testID={tID(testID)} style={style}>
      {!hideTabBar && (
        <TabsWrapper
          hideBottomMargin={tabBarOptions?.hideBottomMargin}
          tabHeight={tabBarOptions?.tabHeight}
          tabHorizontalPadding={tabBarOptions?.tabHorizontalPadding}
          showBorder={tabBarOptions?.showBorder ?? true}
        >
          <HorizontalTabs
            tabs={tabs.map((tab) => ({
              id: tab.id,
              label: tab.label,
              iconWithTooltip: tab.iconWithTooltip,
              badgeCount: tab.badgeCount,
            }))}
            onPress={handleTabPress}
            selectedTabId={activeTabId}
            testID={testID}
            tabStyle={
              tabBarOptions?.tabStyle ??
              (tabBarOptions?.indicatorVariant === IndicatorVariant.PILL ? undefined : { paddingVertical: 12 })
            }
            indicatorVariant={tabBarOptions?.indicatorVariant}
            textCasing={tabBarOptions?.textCasing}
            pressedOpacity={tabBarOptions?.pressedOpacity}
            indicatorHeight={tabBarOptions?.indicatorHeight}
            indicatorBorderRadius={tabBarOptions?.indicatorBorderRadius}
            indicatorColor={tabBarOptions?.indicatorColor}
            inactiveTextColor={tabBarOptions?.inactiveTextColor}
          />
        </TabsWrapper>
      )}
      <ContentContainer
        testID={tID(`${testID}-content`)}
        style={contentStyle}
        overflowTreatment={activeTab ? activeTab?.overflowTreatment : ''}
      >
        {activeTab && activeTab.content}
      </ContentContainer>
    </Container>
  )
}
